import React, { useContext, useState } from 'react';
import { useDispatch, batch, connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as MailActions from '../../actions/mail';
import { getObjectType } from '../../utils/typeMap';
import { SelectedRecordContainerContext } from '../selectedRecordContainer/SelectedRecordContainer';
import { setDocrioFiles, setFileSaveState, setSelectedFiles, navigatingDocrioFiles } from '../../actions/files';
import { navigatingFolders } from '../../actions/folders';
import { Tooltip } from '../shared/Tooltip';
import { renderFileSkeletons } from '../shared/Skeletons/skeletonUtils';
import { Accordion } from '../accordion/Accordion';
import DocrioFile from './DocrioFile';
import { useFileValidation } from '../../utils/fileValidation';
import { HiddenFileValidationBanner } from './HiddenFileValidationBanner';

import './Docrio.scss';

const getSrcByName = (attachment, fileMaps = {}, docrioNamespace = 'litify_docs') => {
  const { name, contentType, folderPaths = {} } = attachment;
  const delimiter = '.';
  // The additional pop is to account for the salesforce which has delimiters of '/' and '.'
  let type = name?.split(delimiter)?.pop()?.split('.')?.pop();
  // If the attached eml file is without the .eml extension
  if (type === name) {
    let fileMap = {};
    if (Object.keys(folderPaths).length) {
      const [recordId = '', record = {}] = Object.entries(folderPaths)[0] || [];
      // Keys come from internal file-map data (Object iteration), not user input.
      // eslint-disable-next-line security/detect-object-injection
      const files = fileMaps?.[recordId]?.[record?.path] || [];
      fileMap = files.find((file) => file.Name.split('.')[0] === name) || {};
    } else {
      outerLoop:
      for (const relatedToId in fileMaps) {
        if (Object.hasOwn(fileMaps, relatedToId)) {
          // eslint-disable-next-line security/detect-object-injection
          const folderMap = fileMaps[relatedToId];
          for (const folder in folderMap) {
            if (Object.hasOwn(folderMap, folder)) {
              // eslint-disable-next-line security/detect-object-injection
              for (const file of folderMap[folder]) {
                if (file.Name.split('.')[0] === name) {
                  fileMap = file;
                  break outerLoop; // Break out of all loops
                }
              }
            }
          }
        }
      }
    }
    if (Object.keys(fileMap).length === 0 && !contentType) {
      type = 'eml';
    } else {
      type = fileMap?.[`${docrioNamespace}__File_Type__c`] === 'message/rfc822' ? 'eml' : 'default';
    }
  }
  return getObjectType(type).icon;
};

const getSrcByType = (value) => {
  const delimiter = '/';
  // The additional pop is to account for the salesforce which has delimiters of '/' and '.'
  return getObjectType(value?.split(delimiter)?.pop()?.split('.')?.pop()).icon;
};

const getImgSrc = (attachment, fileMaps = {}, docrioNamespace = 'litify_docs') => {
  const { contentType } = attachment;
  /**
   * If no content type specified we can skip the first lookup.
   */
  if (!contentType) {
    return getSrcByName(attachment, fileMaps, docrioNamespace);
  }

  /**
   * If there is a specified content type attempt to find src by that.
   * If the result is default, check the name.
   *
   * Note: it is possible for the Office.context.mailbox.item.getAttachmentsAsync
   * call to return both a file name and a garbage contentType (some weird race
   * condition in exchange). To handle this, we need to re-check the file name
   * in the case when the src type results to default icon.
   */
  const asset = getSrcByType(contentType);
  return asset.includes('other.svg') ? getSrcByName(attachment, fileMaps, docrioNamespace) : asset;
};

const Docrio = ({
  isObjectConfigured = true,
  // redux
  files,
  mail,
  draftId,
  skeletonFiles,
  selectedRecords,
  fileMaps,
  saveAllEmailFiles,
  displayEmailFiles,
  saveAllAttachments,
  displayAttachments,
  objectsAndDisplayFields,
  defaultFolderSettings,
  fileValidationErrors,
  user,
  docrioNamespace,
}) => {
  const dispatch = useDispatch();
  const context = useContext(SelectedRecordContainerContext);
  const { isComposingEmail, docrioSubject, emailSubject } = mail;
  const attachments = [...files.filter((file) => file.id !== context.emailId && file.id !== draftId)];
  // Attachments still running through the Doc Assistant pipeline render as skeleton rows below.
  const visibleAttachments = attachments.filter((attachment) => !attachment.daProcessing);
  const processingCount = attachments.length - visibleAttachments.length;
  const docrioEml = { ...files.find((file) => file.id === context.emailId || file.id === draftId) };
  const [isExpanded, setIsExpanded] = useState(true);
  const isAllEmailsSaved = Object.values(selectedRecords).length > 0 && !Object.values(selectedRecords).some((record) => !record.emailMessageRecordSaved && !record.attachments);
  const subject = docrioSubject || emailSubject || '(No Subject)';

  // Collect the distinct violated-rule messages for a set of files.
  const collectValidationErrors = (fileList) => {
    const messages = new Set();
    fileList.forEach((file) => {
      (fileValidationErrors?.[file?.id] || []).forEach((message) => {
        messages.add(message);
      });
    });
    return Array.from(messages);
  };

  // When a file category is hidden from the UI (admin display settings), its rows aren't rendered,
  // so the inline error icon can't be shown. Surface those violations in a banner instead.
  const hiddenEmailErrors = !displayEmailFiles ? collectValidationErrors(docrioEml?.id ? [docrioEml] : []) : [];
  const hiddenAttachmentErrors = !displayAttachments ? collectValidationErrors(attachments) : [];
  const hasHiddenValidationErrors = hiddenEmailErrors.length > 0 || hiddenAttachmentErrors.length > 0;

  // Run the pre-upload dry-run validation whenever candidate files or their save locations change.
  // Pass auto-save context so auto-threaded / auto-saved files (no explicit selection) are covered.
  useFileValidation(files, selectedRecords, mail.ownerEmail, dispatch, {
    saveAllAttachments,
    saveAllEmails: saveAllEmailFiles,
    defaultFolderSettings,
    emailFileIds: new Set([context.emailId, draftId].filter(Boolean)),
  });


  const savedState = (id) => {
    const file = files.find((file) => file.id === id);
    const fileSavedToAllRecords = Object.values(selectedRecords).length > 0 &&
      Object.values(selectedRecords).filter((record) =>
        Array.isArray(record?.attachments) && record?.attachments?.find((attachment) =>
          attachment?.[`${docrioNamespace}__File_Data_Hash__c`] === file.hash)).length === Object.values(selectedRecords).length;

    return !isComposingEmail && ((file?.saved || file?.presaved) || file?.docrioId) && fileSavedToAllRecords;
  };

  const setSubjectAction = (newSubject) => {
    const currentFiles = [...files];
    const eml = currentFiles.find((item) => !item.id || [context.emailId, draftId].includes(item.id));
    eml.name = newSubject;

    batch(() => {
      dispatch(MailActions.setDocrioSubject(newSubject));
      dispatch(MailActions.isEmailEdited(true));
      dispatch(MailActions.isSubjectEdited(true));
      dispatch(setDocrioFiles(currentFiles));
    });
  };
  const updateFile = (newName, itemId) => {
    const currentFiles = [...files];
    const fileToUpdate = currentFiles.find((file) => file.id === itemId);
    fileToUpdate.outlookName = fileToUpdate.name;
    fileToUpdate.name = newName;
    // Re-mark the edited file for saving so that fixing a rule-violating file re-enables the Save
    // button after a partial save (the valid files were saved and this one was left unsaved).
    if (!fileToUpdate.saved && !fileToUpdate.presaved && Object.keys(fileToUpdate.folderPaths || {}).length) {
      fileToUpdate.toSave = true;
    }
    dispatch(setDocrioFiles(currentFiles));
  };

  const getPopoutLink = (file) => {
    const attachments = Object.values(selectedRecords)?.map((record) => record.attachments)?.flat()?.filter((attachment) => attachment?.[`${docrioNamespace}__File_Data_Hash__c`] === file.hash);
    if (attachments.length > 1) {
      return attachments?.map((attachment) => {
        const record = selectedRecords?.[attachment?.[`${docrioNamespace}__File_Info__r.${docrioNamespace}__Related_To__c`]];
        const currentDisplayFields = objectsAndDisplayFields?.find((object) => object.apiName === record?.type)?.displayFields.map((field) => field.apiName) || [];
        return {
          link: `${user.salesforceInstanceUrl}/lightning/n/${docrioNamespace}__File_Details?c__id=${attachment?.[`${docrioNamespace}__File_Info__c`]}`,
          name: record?.[currentDisplayFields[0]] || record?.Id,
        };
      }) || [];
    }
    return file?.docrioId ? `${user.salesforceInstanceUrl}/lightning/n/${docrioNamespace}__File_Details?c__id=${file.docrioId}` : '';
  };


  const selectAttachmentSpan = (
    <span className='docrio-selectAttachmentsSpn'
      onClick={() => {
        batch(() => {
          dispatch(navigatingFolders(true));
          dispatch(navigatingDocrioFiles(true));
          dispatch(setSelectedFiles({}));
        });
      }}>
      <Tooltip textContent={<span>Attach from Docrio</span>}>
        <span>Select Attachments</span>
      </Tooltip>
    </span>);

  return (
    <div id="docrio-folder-component" className={(!displayAttachments && !displayEmailFiles && !hasHiddenValidationErrors) ? 'hide' : ''}>
      <Accordion title='Docrio' link={isComposingEmail ? selectAttachmentSpan : <span></span>} status={isExpanded} setStatus={setIsExpanded} />
      {
        isExpanded && hiddenEmailErrors.length > 0 &&
        <HiddenFileValidationBanner
          testId='hidden-email-validation-banner'
          message='The email will not be saved because it violates one or more validation rules'
          rules={hiddenEmailErrors}
        />
      }
      {
        isExpanded && hiddenAttachmentErrors.length > 0 &&
        <HiddenFileValidationBanner
          testId='hidden-attachment-validation-banner'
          message='Some attachments will not be saved because they violate one or more validation rules'
          rules={hiddenAttachmentErrors}
        />
      }
      {
        isExpanded && <DocrioFile
          key={context.emailId}
          className={displayEmailFiles ? '' : 'docrio-hidden'}
          itemId={draftId || context.emailId}
          nameProp={subject}
          imgSrc='/assets/email.svg'
          folderDisabled={!isObjectConfigured}
          folderPathObj={docrioEml?.folderPaths}
          saveDispatchAction={setFileSaveState}
          savedState={savedState(context.emailId)}
          isSaving={docrioEml?.isSaving || false}
          showPopOut={!context.isComposingEmail && savedState(context.emailId) && isAllEmailsSaved}
          editCallback={setSubjectAction}
          autoSaving={saveAllEmailFiles}
          popoutLink={(!context.isComposingEmail && savedState(context.emailId)) && getPopoutLink(docrioEml)}
          showLocation
          showFolder
        />
      }
      {
        isExpanded && visibleAttachments?.map((attachment) =>
          <DocrioFile
            key={attachment.id}
            className={displayAttachments ? '' : 'docrio-hidden'}
            itemId={attachment.id}
            nameProp={attachment.name}
            imgSrc={getImgSrc(attachment, fileMaps, docrioNamespace)}
            editCallback={updateFile}
            saveDispatchAction={setFileSaveState}
            savedState={savedState(attachment.id)}
            isSaving={attachment.isSaving}
            folderDisabled={!isObjectConfigured}
            folderPathObj={attachment?.folderPaths}
            showPopOut={(!context.isComposingEmail && savedState(attachment.id)) || (context.isComposingEmail && attachment.docrioId)}
            autoSaving={saveAllAttachments}
            popoutLink={(!context.isComposingEmail && savedState(attachment.id) || (context.isComposingEmail && attachment.docrioId)) && getPopoutLink(attachment)}
            showLocation
            showFolder
          />,
        )
      }
      {(skeletonFiles + processingCount) > 0 && renderFileSkeletons(skeletonFiles + processingCount)}
    </div>
  );
};

Docrio.propTypes = {
  isObjectConfigured: PropTypes.bool,
  // redux
  files: PropTypes.array,
  mail: PropTypes.object,
  folderMaps: PropTypes.object,
  draftId: PropTypes.string,
  skeletonFiles: PropTypes.number,
  selectedRecords: PropTypes.object,
  fileMaps: PropTypes.object,
  saveAllEmailFiles: PropTypes.bool,
  displayEmailFiles: PropTypes.bool,
  saveAllAttachments: PropTypes.bool,
  displayAttachments: PropTypes.bool,
  selectAttachmentsByDefault: PropTypes.bool,
  objectsAndDisplayFields: PropTypes.array,
  defaultFolderSettings: PropTypes.object,
  fileValidationErrors: PropTypes.object,
  user: PropTypes.object,
  docrioNamespace: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    files: state.files.docrioFiles,
    mail: state.mail,
    folderMaps: state.folders.folderMaps,
    draftId: state.mail.draftId,
    skeletonFiles: state.files.skeletonFiles,
    selectedRecords: state.mail.selectedRecords,
    fileMaps: state.files.fileMaps,
    saveAllEmailFiles: state.mail.settings?.docrioSettings?.emailFiles?.saveAllEmails,
    displayEmailFiles: state.mail.settings?.docrioSettings?.emailFiles?.displayEmailFiles,
    saveAllAttachments: state.mail.settings?.docrioSettings?.attachments?.saveAllAttachments,
    displayAttachments: state.mail.settings?.docrioSettings?.attachments?.displayAttachments,
    selectAttachmentsByDefault: state.mail.settings?.docrioSettings.attachments?.selectAttachmentsByDefault,
    objectsAndDisplayFields: state.mail.settings?.objectsAndDisplayFields,
    defaultFolderSettings: state.mail.settings?.docrioSettings?.defaultFolderSettings,
    fileValidationErrors: state.files.fileValidationErrors,
    user: state.auth.user,
    docrioNamespace: state.mail.settings?.override?.docrioNamespace || 'litify_docs',
  };
};

const DocrioWithState = connect(mapStateToProps)(Docrio);
export default DocrioWithState;
